import React, { useState, useCallback, useEffect } from 'react';
import { DeleteForever } from '@material-ui/icons';

import * as QS from './question-shared';

export interface OptionalFileUploadQuestionProps extends QS.QuestionProps {
    showValidationMessage: boolean;
    validationLabel?: string;
}

export const areFilesEqual = (left: QS.OptionalFileUploadValue, right: QS.OptionalFileUploadValue): boolean => {
    if (!left && !right) {
        return true;
    }

    if (!left || !right) {
        return false;
    }

    return left.fileType === right.fileType && left.filename === right.filename && left.provideLater === right.provideLater;
};

export const OptionalFileUploadQuestion: React.FC<OptionalFileUploadQuestionProps> = ({
    selector,
    styleName,
    children,
    type,
    value,
    placeholder,
    showValidationMessage,
    validationLabel,
    onChange,
    onChangeQualifier,
    onFileSelected,
    fileType,
    disabled,
}) => {
    const [uploading, setUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string>('');

    const onChangeProvideLater = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            /* istanbul ignore else */
            if (onChange) {
                if (onChangeQualifier !== undefined) {
                    (onChange as QS.onChangeWithQualifierType)(onChangeQualifier, { provideLater: e.target.checked });
                } else {
                    (onChange as QS.onChangeType)({
                        provideLater: e.target.checked,
                    });
                }
            }
        },
        [onChange, onChangeQualifier]
    );

    const onRemoveFile = () => {
        /* istanbul ignore else */
        if (onChange) {
            if (onChangeQualifier !== undefined) {
                (onChange as QS.onChangeWithQualifierType)(onChangeQualifier, {
                    filename: '',
                    provideLater: false,
                });
            } else {
                (onChange as QS.onChangeType)({
                    filename: '',
                    provideLater: false,
                });
            }
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setUploading(true);
        /* istanbul ignore else */
        if (onFileSelected) {
            onFileSelected(fileType, e.target.files[0], onChange as QS.onChangeWithQualifierType)
                .catch((err) => {
                    /* istanbul ignore next */
                    setUploadError(err?.message || err?.toString() || 'upload failed');
                })
                .finally(() => {
                    setUploading(false);
                });
        }
    };

    useEffect(() => {
        if (uploadError) {
            // todo this could be used to display feedback to user like try again or somesuch
            console.error('file upload error', uploadError);
        }
    }, [uploadError]);

    const computedStyle = {
        ...QS.styles.default,
        ...(styleName ? QS.styles[styleName] : {}),
    };

    return (
        <>
            <fieldset
                id={`optional-upload-${selector}`}
                data-testid={`optional-upload-${selector}`}
                style={computedStyle.fieldset}
                className={styleName && QS.classNames[styleName].fieldset}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <legend
                        id={`optional-upload-label-${selector}`}
                        style={{
                            ...computedStyle.legend,
                            alignContent: 'flex-start',
                            flex: 2,
                        }}
                        className={styleName && QS.classNames[styleName].legend}
                    >
                        {children}
                    </legend>

                    {!(value as QS.OptionalFileUploadValue)?.filename && (
                        <>
                            <div
                                style={{
                                    alignContent: 'flex-end',
                                    flex: 2,
                                    marginRight: '10px',
                                }}
                            >
                                <input
                                    type='file'
                                    id={`optional-upload-file-${selector}`}
                                    data-testid={`optional-upload-file-${selector}`}
                                    onChange={handleFileSelect}
                                    className={styleName && QS.classNames[styleName].input}
                                    style={{
                                        ...computedStyle.input,
                                        maxWidth: '100%',
                                    }}
                                    placeholder={placeholder}
                                    aria-labelledby={`optional-upload-label-${selector}`}
                                    disabled={disabled}
                                />
                            </div>
                            <div
                                style={{
                                    alignContent: 'flex-end',
                                    flex: 1,
                                    textAlign: 'right',
                                }}
                            >
                                <label htmlFor={`optional-upload-checkbox-${selector}`}>
                                    <input
                                        type='checkbox'
                                        id={`optional-upload-checkbox-${selector}`}
                                        data-testid={`optional-upload-checkbox-${selector}`}
                                        checked={!!(value as QS.OptionalFileUploadValue)?.provideLater}
                                        onChange={onChangeProvideLater}
                                        disabled={!!(value as QS.OptionalFileUploadValue)?.filename || disabled}
                                    />
                                    Provide Later
                                </label>
                            </div>
                        </>
                    )}
                    {!!(value as QS.OptionalFileUploadValue)?.filename && (
                        <>
                            <div
                                style={{
                                    alignContent: 'flex-end',
                                    flex: 3,
                                    textAlign: 'right',
                                }}
                            >
                                {(value as QS.OptionalFileUploadValue)?.filename}
                            </div>
                            <div
                                style={{
                                    alignContent: 'flex-end',
                                    textAlign: 'left',
                                    paddingLeft: '20px',
                                }}
                                aria-label='Remove file'
                                onClick={onRemoveFile}
                            >
                                <DeleteForever />
                            </div>
                        </>
                    )}
                </div>
                {showValidationMessage && QS.validationMessage(type, validationLabel)}
            </fieldset>
            <div
                data-testid={`file-upload-overlay-${selector}`}
                className='file-upload-overlay'
                style={{ display: uploading ? 'block' : 'none' }}
            >
                <progress></progress>
            </div>
        </>
    );
};
